import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { StratifyDocument } from "@stratify/shared";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux"; // Added import
import { RootState } from "../../../../../store/rootReducer"; // Added import
import { selectDocumentById } from "../../../store/documentsSelectors"; // Added import

interface ParentChildInfoCardProps {
  document: StratifyDocument;
}

export const ParentChildInfoCard: React.FC<ParentChildInfoCardProps> = ({
  document,
}) => {
  const parentId = document.metadata?.parentDocumentId;
  const childIds = document.metadata?.childDocumentIds || []; // Default to empty array

  // Fetch related documents from the store
  const parentDocument = useSelector((state: RootState) =>
    parentId ? selectDocumentById(state, parentId) : undefined
  );
  const childDocuments = useSelector((state: RootState) =>
    childIds
      .map((id: string) => selectDocumentById(state, id)) // Add type for id
      .filter(
        (doc: StratifyDocument | undefined): doc is StratifyDocument => !!doc // Add type for doc and use type predicate
      )
  );

  const hasParent = !!parentDocument;
  const hasChildren = childDocuments.length > 0;

  if (!hasParent && !hasChildren) {
    return null; // Don't render the card if there's no parent or child info
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Related Documents
        </Typography>
        {hasParent && (
          <Box mb={hasChildren ? 2 : 0}>
            <Typography variant="subtitle2" color="text.secondary">
              Parent Document
            </Typography>
            <MuiLink
              component={RouterLink}
              to={`/documents/${parentId}`} // Assuming direct document link structure
              underline="hover"
            >
              {parentDocument?.filename || parentId} {/* Display name or ID */}
            </MuiLink>
          </Box>
        )}
        {hasParent && hasChildren && <Divider sx={{ my: 1 }} />}
        {hasChildren && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Child Documents
            </Typography>
            <List dense disablePadding data-testid="child-documents-list">
              {childDocuments.map((childDoc) => (
                <ListItem key={childDoc.id} disableGutters sx={{ py: 0 }}>
                  <ListItemText
                    primary={
                      <MuiLink
                        component={RouterLink}
                        to={`/documents/${childDoc.id}`} // Use childDoc.id for link
                        underline="hover"
                        data-testid="child-document-link"
                      >
                        {childDoc.filename || childDoc.id}{" "}
                        {/* Display name or ID */}
                      </MuiLink>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
